import { MarkHeading } from '../../../components/MarkControls';
import * as layout from '../../../components/markLayout.module.css';
import {
    MarkChoiceLabel,
    MarkChoice,
    MarkInput,
    MarkLabel
} from '../../../components/MarkControls';
import React, { useId } from 'react';
import P from '@hero/ui-kit/typography/P';
import SegmentPicker from '../../Segments/SegmentPicker';
import type { PickedSegment } from '../../Segments/SegmentPicker';
import type { DripReEntryPolicy, DripTriggerType, SegmentTargetKind } from '../../../types';
export type Step2Data = {
    enrollmentSegmentId: number | null;
    enrollmentSegmentName: string | null;
    reEntryPolicy: DripReEntryPolicy;
    reEntryDays: number;
};
type Props = {
    data: Step2Data;
    onChange: (data: Step2Data) => void;
    triggerType: DripTriggerType;
    errors: string[];
    filterTargetKinds?: SegmentTargetKind[];
};
const Step2Audience: React.FC<Props> = ({
    data,
    onChange,
    triggerType,
    errors,
    filterTargetKinds
}) => {
    const policyGroupId = useId();
    const daysId = useId();
    const isSegment = triggerType === 'SEGMENT';
    const handleSegmentChange = (picked: PickedSegment | null) => {
        onChange({
            ...data,
            enrollmentSegmentId: picked?.segment_id ?? null,
            enrollmentSegmentName: picked?.name ?? null
        });
    };
    if (!isSegment) {
        return (
            <P
                styles={{
                    color: 'var(--mark-muted)'
                }}
            >
                Prospect drips do not use audience segments or re-entry policies.
            </P>
        );
    }
    return (
        <div className={layout.form}>
            <SegmentPicker
                value={data.enrollmentSegmentId}
                onChange={handleSegmentChange}
                filterTargetKinds={filterTargetKinds}
            />
            <fieldset
                aria-labelledby={policyGroupId}
                style={{
                    margin: 0
                }}
                className={layout.fieldset}
            >
                <MarkHeading id={policyGroupId} role="h6">
                    Re-entry policy
                </MarkHeading>
                <div
                    role="radiogroup"
                    aria-labelledby={policyGroupId}
                    className={layout.choiceGroup}
                >
                    <MarkChoiceLabel>
                        <MarkChoice
                            type="radio"
                            name="re-entry-policy"
                            data-field="re-entry-policy"
                            value="once_ever"
                            checked={data.reEntryPolicy === 'once_ever'}
                            onChange={() => onChange({ ...data, reEntryPolicy: 'once_ever' })}
                        />
                        Once ever
                    </MarkChoiceLabel>
                    <MarkChoiceLabel>
                        <MarkChoice
                            type="radio"
                            name="re-entry-policy"
                            value="once_per_n_days"
                            checked={data.reEntryPolicy === 'once_per_n_days'}
                            onChange={() => onChange({ ...data, reEntryPolicy: 'once_per_n_days' })}
                        />
                        Once per N days
                    </MarkChoiceLabel>
                    <MarkChoiceLabel>
                        <MarkChoice
                            type="radio"
                            name="re-entry-policy"
                            value="always"
                            checked={data.reEntryPolicy === 'always'}
                            onChange={() => onChange({ ...data, reEntryPolicy: 'always' })}
                        />
                        Always
                    </MarkChoiceLabel>
                </div>
            </fieldset>
            {data.reEntryPolicy === 'once_per_n_days' && (
                <div className={layout.field}>
                    <MarkLabel htmlFor={daysId}>Re-entry days</MarkLabel>
                    <MarkInput
                        id={daysId}
                        data-field="re-entry-days"
                        type="number"
                        min={1}
                        value={data.reEntryDays}
                        onChange={(e) =>
                            onChange({
                                ...data,
                                reEntryDays: Math.max(
                                    1,
                                    Math.floor(Number(e.currentTarget.value) || 1)
                                )
                            })
                        }
                    />
                </div>
            )}
            {errors.length > 0 && (
                <div role="alert" aria-live="polite" className={layout.notice}>
                    {errors.map((e) => (
                        <P
                            key={e}
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-danger)'
                            }}
                        >
                            {e}
                        </P>
                    ))}
                </div>
            )}
        </div>
    );
};
export default Step2Audience;
